<!-- src/components/NavBar.vue -->
<template>
  <header class="core-shell-nav">
    <ul id="core-side-nav" class="sidenav sidenav-fixed core-sidenav">
      <!-- BRAND -->
      <li class="core-sidenav-brand">
        <router-link
          :to="{ name: 'VenueDashboard' }"
          class="core-sidenav-brand-link"
          @click="closeMobileNav"
        >
          <span class="core-brand-logo-wrap">
            <img :src="coredeckLogo" alt="CoreDeck" class="core-brand-logo" />
          </span>

          <span class="core-brand-copy">
            <span class="core-brand-title">CoreDeck</span>
            <span class="core-brand-subtitle">Operating System</span>
          </span>
        </router-link>
      </li>

      <!-- USER -->
      <li v-if="userLoaded && (displayName || userEmail)" class="core-sidenav-user">
        <div
          class="chip core-user-chip"
          role="button"
          tabindex="0"
          title="Edit profile"
          @click="openProfile"
          @keydown.enter.prevent="openProfile"
          @keydown.space.prevent="openProfile"
        >
          <img v-if="displayPhoto" :src="displayPhoto" alt="User avatar" />

          <span v-else class="core-user-avatar-fallback">
            <i class="material-icons">person</i>
          </span>

          <span class="core-user-chip-name">
            {{ displayName || userEmail }}
          </span>
        </div>
      </li>

      <li>
        <div class="divider"></div>
      </li>

      <!-- DASHBOARD -->
      <li v-if="!isSocialMedia">
        <router-link
          :to="{ name: 'VenueDashboard' }"
          class="core-side-link"
          @click="handleNavClick"
        >
          <i class="material-icons">dashboard</i>
          <span>Dashboard</span>
        </router-link>
      </li>

      <!-- FINANCE -->
      <li
        v-if="!isSocialMedia"
        class="core-flyout-parent"
        @mouseenter="openFlyout('finance')"
        @mouseleave="closeDesktopFlyout"
      >
        <a
          href="#!"
          class="core-side-link core-section-trigger"
          :class="{
            'core-section-open': activeFlyout === 'finance',
            'core-section-active': financeActive,
          }"
          @click.prevent="toggleFlyout('finance')"
        >
          <i class="material-icons">account_balance</i>
          <span>Finance</span>
          <i class="material-icons core-chevron">chevron_right</i>
        </a>

        <div v-show="activeFlyout === 'finance'" class="core-side-flyout">
          <router-link :to="{ name: 'Tills' }" @click="handleNavClick">
            Tills
          </router-link>

          <router-link :to="{ name: 'SafeCount' }" @click="handleNavClick">
            Safe handover
          </router-link>

          <router-link :to="{ name: 'PettyCash' }" @click="handleNavClick">
            Petty Cash
          </router-link>

          <router-link :to="{ name: 'Forecast' }" @click="handleNavClick">
            Forecast
          </router-link>

          <router-link :to="{ name: 'Comparisons' }" @click="handleNavClick">
            Comparisons
          </router-link>

          <router-link :to="{ name: 'EventRoi' }" @click="handleNavClick">
            Event ROI
          </router-link>

          <router-link :to="{ name: 'Utilities' }" @click="handleNavClick">
            Energy Util's
          </router-link>

          <router-link
            v-if="userLoaded && isOperations"
            :to="{ name: 'Budget' }"
            @click="handleNavClick"
          >
            Budget Input
          </router-link>
        </div>
      </li>

      <!-- STOCK -->
      <li
        v-if="!isSocialMedia"
        class="core-flyout-parent"
        @mouseenter="openFlyout('stock')"
        @mouseleave="closeDesktopFlyout"
      >
        <a
          href="#!"
          class="core-side-link core-section-trigger"
          :class="{
            'core-section-open': activeFlyout === 'stock',
            'core-section-active': stockActive,
          }"
          @click.prevent="toggleFlyout('stock')"
        >
          <i class="material-icons">inventory_2</i>
          <span>Stock</span>
          <i class="material-icons core-chevron">chevron_right</i>
        </a>

        <div v-show="activeFlyout === 'stock'" class="core-side-flyout">
          <router-link :to="{ name: 'Stock' }" @click="handleNavClick">
            Stock
          </router-link>

          <router-link :to="{ name: 'DeliveryNotes' }" @click="handleNavClick">
            Delivery Notes
          </router-link>

          <router-link :to="{ name: 'StockReports' }" @click="handleNavClick">
            Stock Reports
          </router-link>
        </div>
      </li>

      <!-- LABOUR -->
      <li
        v-if="!isSocialMedia"
        class="core-flyout-parent"
        @mouseenter="openFlyout('labour')"
        @mouseleave="closeDesktopFlyout"
      >
        <a
          href="#!"
          class="core-side-link core-section-trigger"
          :class="{
            'core-section-open': activeFlyout === 'labour',
            'core-section-active': labourActive,
          }"
          @click.prevent="toggleFlyout('labour')"
        >
          <i class="material-icons">groups</i>
          <span>Labour</span>
          <i class="material-icons core-chevron">chevron_right</i>
        </a>

        <div v-show="activeFlyout === 'labour'" class="core-side-flyout">
          <router-link :to="{ name: 'Rota' }" @click="handleNavClick"> Rota </router-link>

          <router-link :to="{ name: 'WageSummary' }" @click="handleNavClick">
            Wage Summary
          </router-link>

          <router-link
            :to="{
              name: 'HolidayRequests',
              params: $route.params.venueId ? { venueId: $route.params.venueId } : {},
            }"
            @click="handleNavClick"
          >
            Holiday Requests
          </router-link>
        </div>
      </li>

      <!-- DUE DILIGENCE -->
      <li
        v-if="!isSocialMedia"
        class="core-flyout-parent"
        @mouseenter="openFlyout('diligence')"
        @mouseleave="closeDesktopFlyout"
      >
        <a
          href="#!"
          class="core-side-link core-section-trigger"
          :class="{
            'core-section-open': activeFlyout === 'diligence',
            'core-section-active': diligenceActive,
          }"
          @click.prevent="toggleFlyout('diligence')"
        >
          <i class="material-icons">fact_check</i>
          <span>Due Diligence</span>
          <i class="material-icons core-chevron">chevron_right</i>
        </a>

        <div v-show="activeFlyout === 'diligence'" class="core-side-flyout">
          <router-link :to="{ name: 'VenueAudit' }" @click="handleNavClick">
            Venue Audit
          </router-link>

          <router-link :to="{ name: 'IncidentReports' }" @click="handleNavClick">
            Incident Report
          </router-link>

          <router-link :to="{ name: 'DocLibrary' }" @click="handleNavClick">
            Document Library
          </router-link>

          <router-link :to="{ name: 'OpeningChecks' }" @click="handleNavClick">
            Opening Checks
          </router-link>

          <router-link :to="{ name: 'FireSafety' }" @click="handleNavClick">
            Fire Safety
          </router-link>

          <router-link :to="{ name: 'CoreInformation' }" @click="handleNavClick">
            Core Information
          </router-link>

          <!-- OPERATIONS / MULTI-SITE / SINGLE-SITE -->
          <router-link :to="{ name: 'TrainingView' }" @click="handleNavClick">
            Training Progress
          </router-link>
        </div>
      </li>

      <!-- ARTWORK -->
      <li>
        <router-link
          :to="{
            name: 'ArtworkWorkflow',
            params: $route.params.venueId ? { venueId: $route.params.venueId } : {},
          }"
          class="core-side-link"
          @click="handleNavClick"
        >
          <i class="material-icons">palette</i>
          <span>Artwork</span>
        </router-link>
      </li>

      <!-- MAILER -->
      <li v-if="userLoaded && (isOperations || isSocialMedia)">
        <router-link
          :to="{
            name: 'Mailer',
            params: $route.params.venueId ? { venueId: $route.params.venueId } : {},
          }"
          class="core-side-link"
          @click="handleNavClick"
        >
          <i class="material-icons">mail</i>
          <span>Mailer</span>
        </router-link>
      </li>

      <!-- ORDERING -->
      <li v-if="!isSocialMedia">
        <router-link
          :to="{
            name: 'LiquorOrdering',
            params: $route.params.venueId ? { venueId: $route.params.venueId } : {},
          }"
          class="core-side-link"
          @click="handleNavClick"
        >
          <i class="material-icons">local_shipping</i>
          <span>Ordering</span>
        </router-link>
      </li>

      <!-- TASKS -->
      <li v-if="userLoaded && isOperations">
        <router-link
          :to="{
            name: 'Tasks',
            params: $route.params.venueId ? { venueId: $route.params.venueId } : {},
          }"
          class="core-side-link"
          @click="handleNavClick"
        >
          <i class="material-icons">task_alt</i>
          <span>Tasks</span>
        </router-link>
      </li>

      <!-- ADD STAFF -->
      <li v-if="!isSocialMedia && isManager">
        <router-link
          :to="{ name: 'ManagerAddStaff' }"
          class="core-side-link"
          @click="handleNavClick"
        >
          <i class="material-icons">person_add</i>
          <span>Add Staff</span>
        </router-link>
      </li>

      <!-- ADMIN -->
      <li
        v-if="!isSocialMedia && isAdmin"
        class="core-flyout-parent"
        @mouseenter="openFlyout('admin')"
        @mouseleave="closeDesktopFlyout"
      >
        <a
          href="#!"
          class="core-side-link core-section-trigger"
          :class="{
            'core-section-open': activeFlyout === 'admin',
            'core-section-active': adminActive,
          }"
          @click.prevent="toggleFlyout('admin')"
        >
          <i class="material-icons">admin_panel_settings</i>
          <span>Admin</span>
          <i class="material-icons core-chevron">chevron_right</i>
        </a>

        <div v-show="activeFlyout === 'admin'" class="core-side-flyout">
          <router-link :to="{ name: 'AdminUsers' }" @click="handleNavClick">
            User Admin
          </router-link>

          <router-link :to="{ name: 'AdminVenue' }" @click="handleNavClick">
            Venue Admin
          </router-link>
        </div>
      </li>

      <!-- LOGOUT -->
      <li v-if="userLoaded" class="core-sidenav-logout">
        <button
          type="button"
          class="btn red lighten-1 waves-effect waves-light rounded"
          @click.prevent="handleLogout"
        >
          <i class="material-icons">logout</i>
          <span>Logout</span>
        </button>
      </li>
    </ul>

    <!-- TOP BAR -->
    <div class="core-shell-topbar">
      <div class="core-shell-topbar-inner">
        <div class="core-shell-topbar-left">
          <a
            href="#!"
            data-target="core-side-nav"
            class="sidenav-trigger core-shell-mobile-trigger"
            aria-label="Open navigation"
          >
            <i class="material-icons">menu</i>
          </a>

          <div>
            <WeatherStrip />
            <br />
            <h1 class="core-shell-greeting">
              {{ greeting }}<template v-if="greetingName">, {{ greetingName }}</template>
            </h1>
          </div>
        </div>

        <div class="core-shell-topbar-actions">
          <StartEndDayButton v-if="!isSocialMedia" />
        </div>
      </div>
    </div>

    <UserProfileModal ref="profileModalRef" />
  </header>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

import { useRoute } from "vue-router";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { logout } from "@/scripts/logout";
import { auth, db } from "@/firebase";
import UserProfileModal from "@/components/UserProfileModal.vue";
import StartEndDayButton from "./StartEndDayButton.vue";
import WeatherStrip from "@/components/WeatherStrip.vue";
import coredeckLogo from "@/assets/coredeckLogo.png";

const route = useRoute();
const routeName = computed(() => String(route.name || ""));

/* =========================================================
   ACTIVE ROUTE GROUPS
   ========================================================= */

const financeRoutes = [
  "Tills",
  "SafeCount",
  "PettyCash",
  "Forecast",
  "Comparisons",
  "EventRoi",
  "Utilities",
  "Budget",
];

const stockRoutes = ["Stock", "DeliveryNotes", "StockReports"];

const labourRoutes = ["Rota", "WageSummary", "HolidayRequests"];

const diligenceRoutes = [
  "VenueAudit",
  "IncidentReports",
  "DocLibrary",
  "OpeningChecks",
  "FireSafety",
  "CoreInformation",
  "TrainingView",
];

const adminRoutes = ["AdminUsers", "AdminVenue"];

const financeActive = computed(() => financeRoutes.includes(routeName.value));

const stockActive = computed(() => stockRoutes.includes(routeName.value));

const labourActive = computed(() => labourRoutes.includes(routeName.value));

const diligenceActive = computed(() => diligenceRoutes.includes(routeName.value));

const adminActive = computed(() => adminRoutes.includes(routeName.value));

/* =========================================================
   FLYOUT MENUS
   ========================================================= */

const activeFlyout = ref(null);

function openFlyout(name) {
  if (window.innerWidth > 992) {
    activeFlyout.value = name;
  }
}

function closeDesktopFlyout() {
  if (window.innerWidth > 992) {
    activeFlyout.value = null;
  }
}

function toggleFlyout(name) {
  activeFlyout.value = activeFlyout.value === name ? null : name;
}

function closeFlyout() {
  activeFlyout.value = null;
}

/* =========================================================
   USER AND ACCESS
   ========================================================= */

const userName = ref("");
const userEmail = ref("");
const userPhoto = ref("");
const userLoaded = ref(false);

const role = ref(null);
const managerTag = ref(false);
const isOperations = ref(false);
const isSocialMedia = ref(false);

const isAdmin = computed(() => role.value === "admin");

const isManager = computed(
  () => !isOperations.value && !isSocialMedia.value && (isAdmin.value || managerTag.value)
);

const overrides = ref({
  firstName: "",
  lastName: "",
  photoUrl: "",
});

const profileModalRef = ref(null);

let unsubscribeAuth = null;
let unsubscribeUserDoc = null;
let greetingTimer = null;

const currentHour = ref(new Date().getHours());

const greeting = computed(() => {
  const hour = currentHour.value;

  if (hour >= 5 && hour < 12) {
    return "Good Morning";
  }
  if (hour >= 12 && hour < 17) {
    return "Good Afternoon";
  }
  if (hour >= 17 && hour < 21) {
    return "Good Evening";
  }
  return "Good Night";
});

/* =========================================================
   LOAD USER ACCESS
   ========================================================= */

async function loadRoleForUser(user) {
  role.value = null;
  managerTag.value = false;
  isOperations.value = false;
  isSocialMedia.value = false;

  if (!user?.uid) {
    return;
  }

  try {
    const token = await user.getIdTokenResult();
    const claims = token?.claims || {};

    if (claims.admin === true || claims.is_admin === true) {
      role.value = "admin";
    }

    if (
      claims.operations === true ||
      claims.ops === true ||
      claims.is_operations === true
    ) {
      isOperations.value = true;
    }

    if (
      claims.social_media === true ||
      claims.socialMedia === true ||
      claims.is_social_media === true
    ) {
      isSocialMedia.value = true;
    }
  } catch (error) {
    console.warn("[NavBar] token claims load failed:", error);
  }

  const snapshot = await getDoc(doc(db, "users", user.uid));

  if (!snapshot.exists()) {
    return;
  }

  const data = snapshot.data() || {};

  role.value = role.value ?? data.role ?? null;

  if (!isOperations.value) {
    isOperations.value =
      data.isOperations === true ||
      data.is_operations === true ||
      data.operations === true ||
      String(data.user_type || data.userType || "")
        .trim()
        .toLowerCase() === "operations";
  }

  if (!isSocialMedia.value) {
    isSocialMedia.value =
      data.isSocialMedia === true ||
      data.is_social_media === true ||
      data.social_media === true ||
      String(data.user_type || data.userType || "")
        .trim()
        .toLowerCase() === "social_media";
  }

  managerTag.value =
    isOperations.value || isSocialMedia.value ? false : data.managerTag === true;
}

/* =========================================================
   PROFILE LISTENER
   ========================================================= */

function attachUserOverridesListener(uid) {
  if (unsubscribeUserDoc) {
    unsubscribeUserDoc();
  }

  unsubscribeUserDoc = null;

  if (!uid) {
    return;
  }

  unsubscribeUserDoc = onSnapshot(
    doc(db, "users", uid),
    (snapshot) => {
      if (!snapshot.exists()) {
        overrides.value = {
          firstName: "",
          lastName: "",
          photoUrl: "",
        };
        return;
      }

      const data = snapshot.data() || {};

      overrides.value = {
        firstName: data.firstName ?? "",
        lastName: data.lastName ?? "",
        photoUrl: data.photoUrl ?? "",
      };
    },
    (error) => {
      console.warn("[NavBar] profile listener failed:", error);
    }
  );
}

/* =========================================================
   DISPLAY VALUES
   ========================================================= */

const displayName = computed(() => {
  const first = overrides.value.firstName?.trim();
  const last = overrides.value.lastName?.trim();
  const fullName = `${first || ""} ${last || ""}`.trim();

  return fullName || userName.value || userEmail.value || "";
});

const greetingName = computed(() => {
  const first = String(overrides.value.firstName || "").trim();

  if (first) {
    return first;
  }

  const name = String(userName.value || "").trim();

  if (name) {
    return name.split(/\s+/)[0] || name;
  }

  const email = String(userEmail.value || "").trim();

  return email ? email.split("@")[0] || email : "";
});

const displayPhoto = computed(() => overrides.value.photoUrl || userPhoto.value || "");

/* =========================================================
   MATERIALIZE
   ========================================================= */

function destroyMaterialize() {
  if (!window.M) {
    return;
  }

  try {
    const sidenav = document.getElementById("core-side-nav");

    if (sidenav) {
      window.M.Sidenav?.getInstance(sidenav)?.destroy();
    }

    const modal = document.getElementById("userProfileModal");

    if (modal) {
      window.M.Modal?.getInstance(modal)?.destroy();
    }
  } catch (error) {
    console.warn("[NavBar] destroy warning:", error);
  }
}

function initMaterialize() {
  if (!window.M) {
    return;
  }

  try {
    const sidenav = document.getElementById("core-side-nav");

    if (sidenav && window.M.Sidenav) {
      window.M.Sidenav.init(sidenav, {
        edge: "left",
        draggable: true,
        inDuration: 180,
        outDuration: 150,
      });
    }

    const modal = document.getElementById("userProfileModal");

    if (modal && window.M.Modal) {
      window.M.Modal.init(modal, {
        dismissible: true,
        opacity: 0.5,
        inDuration: 150,
        outDuration: 120,
      });
    }
  } catch (error) {
    console.warn("[NavBar] init warning:", error);
  }
}

async function initialiseMaterialize() {
  await nextTick();
  destroyMaterialize();
  initMaterialize();
}

/* =========================================================
   MOBILE NAVIGATION
   ========================================================= */

function closeMobileNav() {
  if (window.innerWidth > 992) {
    return;
  }

  const element = document.getElementById("core-side-nav");

  if (!element) {
    return;
  }

  window.M?.Sidenav?.getInstance(element)?.close();
}

function handleNavClick() {
  closeFlyout();
  closeMobileNav();
}

/* =========================================================
   LOGOUT
   ========================================================= */

async function handleLogout() {
  try {
    closeFlyout();
    closeMobileNav();
    await logout();
  } catch (error) {
    console.error("Logout failed:", error);
  }
}

/* =========================================================
   PROFILE MODAL
   ========================================================= */

async function openProfile() {
  try {
    closeFlyout();
    closeMobileNav();

    await nextTick();

    profileModalRef.value?.load?.();

    const element = document.getElementById("userProfileModal");

    if (!element) {
      return;
    }

    let instance = window.M?.Modal?.getInstance(element);

    if (!instance && window.M?.Modal) {
      instance = window.M.Modal.init(element, {
        dismissible: true,
        opacity: 0.5,
        inDuration: 150,
        outDuration: 120,
      });
    }

    instance?.open();
  } catch (error) {
    console.error("[NavBar] profile modal failed:", error);
  }
}

/* =========================================================
   ROUTE WATCHER
   ========================================================= */

watch(
  () => route.fullPath,
  () => {
    closeFlyout();
    closeMobileNav();
  }
);

/* =========================================================
   MOUNT / CLEANUP
   ========================================================= */

onMounted(() => {
  currentHour.value = new Date().getHours();

  greetingTimer = window.setInterval(() => {
    currentHour.value = new Date().getHours();
  }, 60000);

  unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
    if (user) {
      userName.value = user.displayName || "";
      userEmail.value = user.email || "";
      userPhoto.value = user.photoURL || "";
      userLoaded.value = true;

      attachUserOverridesListener(user.uid);

      try {
        await loadRoleForUser(user);
      } catch (error) {
        console.error("[NavBar] role loading failed:", error);
      }
    } else {
      userName.value = "";
      userEmail.value = "";
      userPhoto.value = "";
      userLoaded.value = false;

      overrides.value = {
        firstName: "",
        lastName: "",
        photoUrl: "",
      };

      role.value = null;
      managerTag.value = false;
      isOperations.value = false;
      isSocialMedia.value = false;

      if (unsubscribeUserDoc) {
        unsubscribeUserDoc();
      }

      unsubscribeUserDoc = null;
    }

    await initialiseMaterialize();
  });

  initialiseMaterialize();
});

onUnmounted(() => {
  if (unsubscribeAuth) {
    unsubscribeAuth();
  }

  if (unsubscribeUserDoc) {
    unsubscribeUserDoc();
  }

  if (greetingTimer) {
    clearInterval(greetingTimer);
  }

  destroyMaterialize();
});
</script>

<style scoped>
.core-shell-nav {
  --sidebar-width: 270px;
  --sidebar-bg: #17231f;
  --sidebar-active: #293b35;
  --sidebar-hover: rgba(255, 255, 255, 0.06);
  --sidebar-text: #edf2f0;
  --sidebar-muted: #aebbb7;
}

.core-shell-topbar {
  min-height: 116px;
  margin-left: var(--sidebar-width);
  display: flex;
  align-items: center;
  background: #f7f8fa;
  border-bottom: 1px solid #e0e4e6;
}

.core-shell-topbar-inner {
  width: 92%;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.core-shell-topbar-left {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 16px;
}

.core-shell-mobile-trigger {
  display: none;
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  min-height: 44px !important;
  padding: 0 !important;
  align-items: center;
  justify-content: center;
  border: 1px solid #dfe3e5 !important;
  border-radius: 9px !important;
  background: #ffffff !important;
  color: #263238 !important;
}

.core-shell-mobile-trigger .material-icons {
  margin: 0 !important;
}

.core-shell-eyebrow {
  margin: 0 0 5px;
  color: #7c8389;
  font-size: 0.76rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.core-shell-greeting {
  margin: 0;
  color: #20252b;
  font-size: clamp(2rem, 2.8vw, 2.6rem);
  font-weight: 400;
  line-height: 1.05;
}

.core-shell-topbar-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.core-sidenav.sidenav {
  width: var(--sidebar-width);
  padding: 0 0 14px;
  background: var(--sidebar-bg);
  box-shadow: none;
  overflow-x: hidden;
  overflow-y: auto;
}

.core-sidenav.sidenav-fixed {
  z-index: 1000;
}

.core-sidenav li {
  min-height: 0 !important;
  line-height: normal !important;
}

.core-sidenav a {
  line-height: normal;
}

.core-sidenav-brand {
  margin: 0 !important;
  padding: 18px 18px 14px !important;
}

.core-sidenav-brand-link {
  width: 100%;
  min-height: 62px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  gap: 13px;
  padding: 0 !important;
  background: transparent !important;
  color: #ffffff !important;
}

.core-sidenav-brand-link:hover {
  background: transparent !important;
}

.core-brand-logo-wrap {
  width: 58px;
  height: 58px;
  flex: 0 0 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.core-brand-logo {
  display: block;
  width: 58px !important;
  height: 58px !important;
  max-width: 58px !important;
  object-fit: contain;
}

.core-brand-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  text-align: left;
}

.core-brand-title {
  color: #ffffff;
  font-size: 1.18rem;
  font-weight: 500;
  line-height: 1.05;
}

.core-brand-subtitle {
  margin-top: 6px;
  color: var(--sidebar-muted);
  font-size: 0.61rem;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.17em;
  text-transform: uppercase;
  white-space: nowrap;
}

.core-sidenav-user {
  margin: 0 !important;
  padding: 4px 16px 13px !important;
}

.core-user-chip {
  width: 100% !important;
  max-width: 100% !important;
  height: 48px !important;
  min-height: 48px !important;
  margin: 0 !important;
  padding: 0 13px 0 0 !important;
  display: flex !important;
  align-items: center !important;
  border-radius: 24px !important;
  overflow: hidden;
  background: #ffffff !important;
  color: #263238 !important;
  white-space: nowrap !important;
  cursor: pointer;
}

.core-user-chip:hover {
  background: #eceff1 !important;
}

.core-user-chip img {
  width: 48px !important;
  height: 48px !important;
  max-width: 48px !important;
  flex: 0 0 48px;
  margin: 0 11px 0 0 !important;
  object-fit: cover;
}

.core-user-avatar-fallback {
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  margin-right: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #cfd8dc;
  color: #37474f;
}

.core-user-avatar-fallback .material-icons {
  margin: 0 !important;
  font-size: 25px !important;
}

.core-user-chip-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.9rem;
  font-weight: 500;
}

.core-sidenav .divider {
  height: 1px;
  margin: 0 16px 8px;
  background: rgba(255, 255, 255, 0.1);
}

.core-side-link {
  width: calc(100% - 20px);
  height: 46px !important;
  min-height: 46px !important;
  margin: 2px 10px !important;
  padding: 0 13px !important;
  display: grid !important;
  grid-template-columns: 26px minmax(0, 1fr) 20px;
  align-items: center;
  column-gap: 11px;
  border: 0 !important;
  border-radius: 8px;
  background: transparent !important;
  color: var(--sidebar-text) !important;
  font-size: 0.96rem;
  font-weight: 400;
  line-height: 1 !important;
}

a.core-side-link:not(.core-section-trigger) {
  grid-template-columns: 26px minmax(0, 1fr);
}

.core-side-link > .material-icons {
  width: 26px !important;
  height: 26px !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  color: #d3dcda !important;
  font-size: 22px !important;
  line-height: 26px !important;
}

.core-side-link > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.core-side-link > .core-chevron {
  width: 20px !important;
  height: 20px !important;
  margin: 0 !important;
  justify-self: end;
  font-size: 20px !important;
  line-height: 20px !important;
}

.core-side-link:hover,
.core-section-open {
  background: var(--sidebar-hover) !important;
}

.core-side-link.router-link-active,
.core-side-link.router-link-exact-active,
.core-section-active {
  background: var(--sidebar-active) !important;
  color: #ffffff !important;
}

.core-flyout-parent {
  position: relative;
}

.core-side-flyout {
  position: absolute;
  top: 0;
  left: calc(100% - 2px);
  width: 220px;
  z-index: 3000;
  padding: 7px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0 10px 10px 0;
  background: #1d2b27;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.24);
}

.core-side-flyout a {
  width: 100%;
  height: 40px !important;
  min-height: 40px !important;
  margin: 0 !important;
  padding: 0 13px !important;
  display: flex !important;
  align-items: center;
  border-radius: 6px;
  background: transparent !important;
  color: #cbd5d2 !important;
  font-size: 0.9rem;
  font-weight: 400;
  line-height: 40px !important;
  white-space: nowrap;
}

.core-side-flyout a:hover {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.07) !important;
}

.core-side-flyout a.router-link-active,
.core-side-flyout a.router-link-exact-active {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.11) !important;
  font-weight: 500;
}

.core-sidenav-logout {
  margin: 10px 0 0 !important;
  padding: 0 10px !important;
}

.core-sidenav-logout .btn {
  width: 100% !important;
  min-height: 39px !important;
  height: 39px !important;
  line-height: 39px !important;
  margin: 0 !important;
  padding: 0 12px !important;
  font-size: 0.82rem !important;
}

.core-sidenav-logout .material-icons {
  margin: 0 !important;
  font-size: 18px !important;
}

@media (min-width: 993px) {
  .core-sidenav.sidenav {
    overflow: visible !important;
  }

  .core-sidenav.sidenav-fixed {
    transform: translateX(0%) !important;
  }
}

@media (max-width: 992px) {
  .core-shell-topbar {
    margin-left: 0;
  }

  .core-shell-mobile-trigger {
    display: inline-flex !important;
  }

  .core-sidenav.sidenav {
    width: min(86vw, 290px);
    overflow-x: hidden !important;
    overflow-y: auto !important;
  }

  .core-side-flyout {
    position: static;
    width: calc(100% - 20px);
    margin: 0 10px 4px;
    padding: 3px 0 5px;
    border: 0;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.1);
    box-shadow: none;
  }

  .core-side-flyout a {
    height: 37px !important;
    min-height: 37px !important;
    padding: 0 12px 0 50px !important;
    font-size: 0.86rem;
    line-height: 37px !important;
  }

  .core-section-open .core-chevron {
    transform: rotate(90deg);
  }
}

@media (max-width: 600px) {
  .core-shell-topbar {
    min-height: 142px;
  }

  .core-shell-topbar-inner {
    width: calc(100% - 28px);
    padding: 17px 0;
    flex-direction: column;
    align-items: stretch;
    gap: 13px;
  }

  .core-shell-topbar-left {
    align-items: flex-start;
  }

  .core-shell-mobile-trigger {
    width: 40px !important;
    min-width: 40px !important;
    height: 40px !important;
    min-height: 40px !important;
    flex-basis: 40px;
  }

  .core-shell-eyebrow {
    font-size: 0.63rem;
    letter-spacing: 0.16em;
  }

  .core-shell-greeting {
    font-size: 1.7rem;
  }

  .core-shell-topbar-actions {
    justify-content: flex-start;
    padding-left: 56px;
  }

  .core-user-chip {
    height: 48px !important;
    min-height: 48px !important;
    padding: 0 13px 0 0 !important;
    line-height: normal !important;
    white-space: nowrap !important;
  }
}
</style>
